import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { MoreVertical } from 'lucide-react';
import { CertificationCardPreview } from './certification-card-preview';
import { useTranslation } from 'react-i18next';

import {
  documentStatusLabels,
  documentStatusTones,
  documentTypeLabels,
  type CrmCustomerDocumentType,
  type CrmCustomerDocumentStatus,
} from '@features/crm-customer-documents';
import { formatEducationModuleScore } from '@entities/education-module';
import { getRegistrationPaymentStatusTone } from '@entities/registration';
import {
  crmCustomerLearningEmLogsPageSize,
  useCrmCustomerLearning,
  type CertificationCollectionResponse,
  type CourseCollectionResponse,
  type CrmCustomerLearningFilters,
  type EducationModuleLogCollectionResponse,
  type CrmCustomerLearningSection,
  type CrmCustomerLearningSort,
  type RegistrationCollectionResponse,
} from '@features/crm-customer-learning/api';
import {
  getCourseStatusTone,
  getLearningValueLabel,
  getResultTone,
} from '@features/crm-customer-learning/model';
import { isApiError } from '@shared/api/errors';
import { env } from '@shared/config';
import { getValidLocale } from '@shared/config/i18n';
import { formatCurrency, useDebouncedUrlSearch } from '@shared/lib';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeaderRow,
  DashboardSectionTabs,
  DescriptionList,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  FilterPopover,
  FilterPopoverFields,
  FilterPopoverInput,
  FilterPopoverSelect,
  Heading,
  HStack,
  PaginationControls,
  ResponsiveTable,
  SearchFilterToolbar,
  SearchInput,
  SortableHeaderButton,
  StatusBadge,
  Text,
  type ResponsiveTableColumn,
} from '@shared/ui';

type Translate = (key: string, defaultValue: string, options?: Record<string, unknown>) => string;
type Registration = RegistrationCollectionResponse['data']['items'][number];
type CanonicalCourse = CourseCollectionResponse['data']['items'][number];
type Course = CanonicalCourse;
type Certification = CertificationCollectionResponse['data']['items'][number];
type EmLog = EducationModuleLogCollectionResponse['data']['logs'][number];
type CoursePresentation = {
  type?: { name: string | null } | null;
  name?: string | null;
  number: string | null;
  startAt: string | null;
  title?: string | null;
};
type LearningSortKey =
  | 'attemptDate'
  | 'attemptResult'
  | 'attemptScore'
  | 'certificationCode'
  | 'certifiedAt'
  | 'cost'
  | 'courseStartAt'
  | 'expiresAt'
  | 'instructorPaymentStatus'
  | 'referenceType'
  | 'registrationNumber'
  | 'registrationType'
  | 'studentPaymentStatus'
  | 'submittedAt';
type RelatedDocument = {
  id: string;
  status: string | null;
  type: string | null;
};
type RelatedDocumentLabels = {
  documentLabel: (type: string | null, index: number) => string;
  emptyLabel: string;
  statusLabel: (status: string | null) => string;
};
type NumberedPageMeta = {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
};

function toText(value: unknown, fallback: string) {
  return typeof value === 'string' ? value : fallback;
}

function numberedPageMeta(value: unknown): NumberedPageMeta | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const candidate = value as Partial<NumberedPageMeta>;
  return typeof candidate.page === 'number' &&
    typeof candidate.pageSize === 'number' &&
    typeof candidate.totalItems === 'number' &&
    typeof candidate.totalPages === 'number' &&
    typeof candidate.hasNextPage === 'boolean' &&
    typeof candidate.hasPreviousPage === 'boolean'
    ? (candidate as NumberedPageMeta)
    : undefined;
}

function formatDate(value: string | null, locale: string) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

function formatNumber(value: number | null, locale: string) {
  return value === null
    ? null
    : new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
}

function resolveCertificatePreviewHref(href: string) {
  return new URL(href, env.VITE_API_URL).toString();
}

function OptionalValue({ fallback, value }: { fallback: string; value: ReactNode }) {
  const isEmpty =
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim().length === 0);
  return isEmpty ? (
    <Text as="span" size="sm" color="emptyValue">
      {fallback}
    </Text>
  ) : (
    value
  );
}

function LearningSectionTabs({
  onSectionChange,
  section,
  translate,
}: {
  onSectionChange: (section: CrmCustomerLearningSection) => void;
  section: CrmCustomerLearningSection;
  translate: Translate;
}) {
  const items: Array<{ id: CrmCustomerLearningSection; label: string }> = [
    {
      id: 'courses',
      label: translate('crm.customerLearning.sections.courses', 'Courses'),
    },
    {
      id: 'registrations',
      label: translate('crm.customerLearning.sections.registrations', 'Registrations'),
    },
    {
      id: 'certifications',
      label: translate('crm.customerLearning.sections.certifications', 'Certifications'),
    },
    {
      id: 'attempts',
      label: translate('crm.customerLearning.sections.attempts', 'EM logs'),
    },
  ];

  return (
    <nav aria-label={translate('crm.customerLearning.title', 'Learning')}>
      <DashboardSectionTabs
        items={items}
        value={section}
        onValueChange={(value) => {
          onSectionChange(value as CrmCustomerLearningSection);
        }}
      />
    </nav>
  );
}

function ResultBadge({ fallback, result }: { fallback: string; result: string | null }) {
  const { t } = useTranslation();
  return result ? (
    <StatusBadge tone={getResultTone(result)}>
      {getLearningValueLabel('result', result, (key, defaultValue) => t(key, { defaultValue }))}
    </StatusBadge>
  ) : (
    <OptionalValue fallback={fallback} value={null} />
  );
}

function CourseStatusBadge({ fallback, status }: { fallback: string; status: string | null }) {
  const { t } = useTranslation();
  return status ? (
    <StatusBadge tone={getCourseStatusTone(status)}>
      {getLearningValueLabel('course', status, (key, defaultValue) => t(key, { defaultValue }))}
    </StatusBadge>
  ) : (
    <OptionalValue fallback={fallback} value={null} />
  );
}

function PaymentStatusBadge({ fallback, status }: { fallback: string; status: string | null }) {
  return status ? (
    <StatusBadge tone={getRegistrationPaymentStatusTone(status)}>{status}</StatusBadge>
  ) : (
    <OptionalValue fallback={fallback} value={null} />
  );
}

function joinDisplayValues(values: Array<string | null | undefined>) {
  const visibleValues = values.filter(
    (value): value is string => typeof value === 'string' && value.trim().length > 0
  );
  return visibleValues.length > 0 ? visibleValues.join(' · ') : null;
}

function formatCoursePresentation({
  course,
  locale,
  translate,
}: {
  course: CoursePresentation | null;
  locale: string;
  translate: Translate;
}) {
  if (course === null) return null;
  const date = formatDate(course.startAt, locale);
  const name = course.type?.name ?? null;
  if (name && course.number && date) {
    return translate(
      'crm.customerLearning.coursePresentation',
      '{{name}} №{{number}} on {{date}}',
      { date, name, number: course.number }
    );
  }
  return joinDisplayValues([name, course.number, date]);
}

function LearningRelatedDocuments({
  documents,
  labels,
}: {
  documents: readonly RelatedDocument[];
  labels: RelatedDocumentLabels;
}) {
  if (documents.length === 0) {
    return (
      <Text as="span" size="sm" color="emptyValue">
        {labels.emptyLabel}
      </Text>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {documents.map((document, index) => (
        <HStack key={document.id} gap="xs" align="center" className="flex-wrap">
          <Text as="span" size="sm">
            {labels.documentLabel(document.type, index)}
          </Text>
          <Text as="span" size="sm" color="muted" aria-hidden="true">
            ·
          </Text>
          <StatusBadge
            tone={
              document.status !== null && document.status in documentStatusTones
                ? documentStatusTones[document.status as CrmCustomerDocumentStatus]
                : 'neutral'
            }
          >
            {labels.statusLabel(document.status)}
          </StatusBadge>
        </HStack>
      ))}
    </div>
  );
}

type LearningRowAction = {
  description?: string;
  disabled?: boolean;
  href?: string;
  label: string;
  onSelect?: () => void;
};

function LearningRowActions({
  actions,
  label,
}: {
  actions: readonly LearningRowAction[];
  label: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={label}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
        >
          <MoreVertical aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((action) =>
          action.href && !action.disabled ? (
            <DropdownMenuItem key={action.label} asChild>
              <a href={action.href} target="_blank" rel="noopener noreferrer">
                {action.label}
              </a>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              key={action.label}
              disabled={action.disabled}
              onSelect={action.onSelect}
            >
              {action.label}
              {action.description ? <span className="sr-only"> {action.description}</span> : null}
            </DropdownMenuItem>
          )
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function formatPassedCount({
  passed,
  total,
  translate,
}: {
  passed: number;
  total: number;
  translate: Translate;
}) {
  return translate('crm.customerLearning.fields.passedCount', '{{passed}}/{{total}} Passed', {
    passed,
    total,
  });
}

/** The canonical Registration summary carries one owned main-exam projection. */
function mainExamPassedCount(registration: Registration) {
  return {
    passed: registration.mainExam.passedAt === null ? 0 : 1,
    total: registration.mainExam.educationModule === null ? 0 : 1,
  };
}

function RegistrationActions({
  registration,
  onViewAttempts,
  onViewDocuments,
  translate,
}: {
  registration: Registration;
  onViewAttempts: () => void;
  onViewDocuments: () => void;
  translate: Translate;
}) {
  const registrationLabel =
    registration.course?.type?.name ??
    registration.registrationNumber ??
    translate('crm.missingValue', 'Not provided');

  return (
    <LearningRowActions
      label={translate('crm.customerLearning.actions.openMenu', 'Open actions for {{record}}', {
        record: registrationLabel,
      })}
      actions={[
        {
          label: translate('crm.customerLearning.actions.viewAttempts', 'View attempts'),
          onSelect: onViewAttempts,
        },
        ...(registration.relatedDocuments.length > 0
          ? [
              {
                label: translate('crm.customerLearning.actions.viewDocuments', 'View documents'),
                onSelect: onViewDocuments,
              },
            ]
          : []),
      ]}
    />
  );
}

function EmLogScore({ log, fallback, locale }: { log: EmLog; fallback: string; locale: string }) {
  return (
    <OptionalValue fallback={fallback} value={formatEducationModuleScore(log.score, locale)} />
  );
}

function EmLogDocumentCount({
  documentCount,
  fallback,
  locale,
}: {
  documentCount: number | null;
  fallback: string;
  locale: string;
}) {
  return <OptionalValue fallback={fallback} value={formatNumber(documentCount, locale)} />;
}

export interface CrmCustomerLearningProps {
  attemptCursor?: string;
  attemptCursorHistory?: string[];
  customerId: string;
  filters: CrmCustomerLearningFilters;
  onFiltersChange: (filters: CrmCustomerLearningFilters) => void;
  onAuthorizationFailure?: () => Promise<void>;
  onAttemptCursorChange: (cursor: string | undefined, history: string[] | undefined) => void;
  onOpenCertificationProfile: (certificationId: string) => void;
  onOpenEducationModuleLogProfile: (educationModuleLogId: string) => void;
  onOpenRegistrationProfile: (registrationId: string) => void;
  onPageChange: (page: number) => void;
  onSearchChange: (q: string | undefined) => void;
  onSectionChange: (section: CrmCustomerLearningSection) => void;
  onSortChange: (sort: CrmCustomerLearningSort) => void;
  onViewCourseRegistrations: (courseId: string) => void;
  onViewCourseProfile: (courseId: string) => void;
  onViewRegistrationDocuments: (registrationId: string) => void;
  onViewRegistration: (registrationNumber: string) => void;
  page: number;
  q?: string;
  section: CrmCustomerLearningSection;
  sort: CrmCustomerLearningSort;
  viewerAccessScope: string;
}

export function CrmCustomerLearning({
  attemptCursor,
  attemptCursorHistory,
  customerId,
  filters,
  onFiltersChange,
  onAuthorizationFailure,
  onAttemptCursorChange,
  onOpenCertificationProfile,
  onOpenEducationModuleLogProfile,
  onOpenRegistrationProfile,
  onPageChange,
  onSearchChange,
  onSectionChange,
  onSortChange,
  onViewCourseRegistrations,
  onViewCourseProfile,
  onViewRegistrationDocuments,
  onViewRegistration,
  page,
  q,
  section,
  sort,
  viewerAccessScope,
}: CrmCustomerLearningProps) {
  const { i18n, t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const apiLocale = getValidLocale(locale);
  const translate = useCallback<Translate>(
    (key, fallback, options) => toText(t(key, { defaultValue: fallback, ...options }), fallback),
    [t]
  );
  const [filterControlsOpen, setFilterControlsOpen] = useState(false);
  const [certificatePreview, setCertificatePreview] = useState<{
    href: string;
    customerId: string;
    viewerAccessScope: string;
  } | null>(null);
  useEffect(() => {
    setCertificatePreview(null);
  }, [customerId, section, viewerAccessScope]);
  const openCertificatePreview = (certification: Certification) => {
    if (certification.certificatePreview.state === 'ready') {
      setCertificatePreview({
        href: resolveCertificatePreviewHref(certification.certificatePreview.href),
        customerId,
        viewerAccessScope,
      });
    }
  };
  const [courseQuery, setCourseQuery] = useState('');
  const [instructorQuery, setInstructorQuery] = useState('');
  const [registrationQuery, setRegistrationQuery] = useState('');
  const query = useCrmCustomerLearning(
    {
      ...filters,
      cursor: section === 'attempts' ? attemptCursor : undefined,
      customerId,
      locale: apiLocale,
      page,
      q,
      section,
      sort,
      viewerAccessScope,
    },
    { courseQuery, filterControlsOpen, instructorQuery, registrationQuery }
  );
  useEffect(() => {
    if (isApiError(query.error) && query.error.status === 403) {
      void onAuthorizationFailure?.();
    }
  }, [onAuthorizationFailure, query.error]);
  const { draft, isTransitionPending, onCompositionEnd, onCompositionStart, setDraft } =
    useDebouncedUrlSearch({ committedQuery: q, onCommit: onSearchChange });
  const fallback = translate('crm.missingValue', 'Not provided');
  const registrationPublicSortKeys = new Set<LearningSortKey>([
    'submittedAt',
    'certifiedAt',
    'registrationNumber',
    'studentPaymentStatus',
    'instructorPaymentStatus',
    'cost',
    'referenceType',
    'registrationType',
  ]);
  const [sortKey, sortDirection] = sort.includes('.')
    ? (sort.split('.') as [LearningSortKey, 'asc' | 'desc'])
    : ([sort.replace(/^-/, ''), sort.startsWith('-') ? 'desc' : 'asc'] as [
        LearningSortKey,
        'asc' | 'desc',
      ]);
  const toggleSort = useCallback(
    (nextSortKey: LearningSortKey) => {
      const nextDirection = sortKey === nextSortKey && sortDirection === 'asc' ? 'desc' : 'asc';
      const usesPublicSort =
        (section === 'courses' && nextSortKey === 'courseStartAt') ||
        (section === 'registrations' && registrationPublicSortKeys.has(nextSortKey));
      const nextSort = usesPublicSort
        ? nextDirection === 'desc'
          ? `-${nextSortKey}`
          : nextSortKey
        : `${nextSortKey}.${nextDirection}`;
      onSortChange(nextSort as CrmCustomerLearningSort);
    },
    [onSortChange, registrationPublicSortKeys, section, sortDirection, sortKey]
  );

  const errorStatus = isApiError(query.error) ? query.error.status : undefined;
  const hasLoadedData =
    section === 'attempts'
      ? query.emLogs !== undefined
      : section === 'courses'
        ? query.courseCollection !== undefined
        : section === 'registrations'
          ? query.registrationCollection !== undefined
          : query.certificationCollection !== undefined;
  if ((!hasLoadedData && !query.isLoading) || errorStatus === 403 || errorStatus === 404) {
    const status = errorStatus;
    const isTimeout =
      isApiError(query.error) &&
      (query.error.status === 504 || query.error.code === 'REQUEST_TIMEOUT');
    const title =
      status === 403
        ? translate('crm.customerLearning.deniedTitle', 'Learning access required')
        : status === 404
          ? translate('crm.customerLearning.notFoundTitle', 'Customer not found')
          : isTimeout
            ? translate('crm.customerLearning.timeoutTitle', 'Learning took too long')
            : translate('crm.customerLearning.unavailableTitle', 'Learning unavailable');
    const description =
      status === 403
        ? translate(
            'crm.customerLearning.deniedDescription',
            'You do not have permission to view this customer’s learning records.'
          )
        : status === 404
          ? translate(
              'crm.customerLearning.notFoundDescription',
              'This customer could not be found.'
            )
          : isTimeout
            ? translate(
                'crm.customerLearning.timeoutDescription',
                'Learning records took too long to load. Try again.'
              )
            : translate(
                'crm.customerLearning.unavailableDescription',
                'Learning records are temporarily unavailable.'
              );
    return (
      <Alert variant="destructive">
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription className="space-y-3">
          <p>{description}</p>
          {status !== 403 && status !== 404 ? (
            <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
              {translate('common.retry', 'Retry')}
            </Button>
          ) : null}
        </AlertDescription>
      </Alert>
    );
  }

  const queryErrorStatus = isApiError(query.error) ? query.error.status : undefined;
  const queryTimedOut =
    isApiError(query.error) &&
    (query.error.status === 504 || query.error.code === 'REQUEST_TIMEOUT');
  const courses = [...(query.courseCollection?.data.items ?? [])];
  const registrations = [...(query.registrationCollection?.data.items ?? [])];
  const certifications = [...(query.certificationCollection?.data.items ?? [])];
  const emLogs = query.emLogs?.data.logs ?? [];
  const emLogFilterValues = {
    results: query.emLogResults?.data.results ?? [],
    types: query.emLogTypes?.data.types ?? [],
  };
  const loadingRows = query.isLoading || query.isFetching || isTransitionPending;
  const activeFilterCount = [
    filters.certificationTypeId,
    filters.certifiedFrom,
    filters.certifiedTo,
    filters.costMax,
    filters.costMin,
    filters.courseId,
    filters.courseStatus,
    filters.courseTypeId,
    filters.dateFrom,
    filters.dateTo,
    filters.attemptedFrom,
    filters.attemptedTo,
    filters.instructorId,
    filters.instructorPaymentStatus,
    filters.registrationId,
    filters.registrationReferenceType,
    filters.registrationType,
    filters.result,
    filters.schoolId,
    filters.studentPaymentStatus,
    filters.submittedFrom,
    filters.submittedTo,
    filters.type,
  ].filter((value) => value !== undefined && value !== '').length;
  const hasContext = filters.registrationId !== undefined || filters.learningItemId !== undefined;
  const hasActiveQuery = (q?.length ?? 0) > 0 || activeFilterCount > 0 || hasContext;
  const emptyState = {
    title: hasActiveQuery
      ? translate('crm.customerLearning.filteredEmptyTitle', 'No matching learning records')
      : translate('crm.customerLearning.emptyTitle', 'No learning records'),
    description: hasActiveQuery
      ? translate(
          'crm.customerLearning.filteredEmptyDescription',
          'Try changing the search or filters.'
        )
      : translate(
          'crm.customerLearning.emptyDescription',
          'No learning history is available for this customer.'
        ),
  };
  const allLabel = translate('crm.customerLearning.filters.all', 'All');
  const selectorState = (selector: {
    isLoading: boolean;
    isError: boolean;
    refetch: () => Promise<unknown>;
  }) => ({
    loading: selector.isLoading,
    loadingLabel: translate('common.loading', 'Loading...'),
    emptyText: translate('crm.customerDirectory.filters.noOptions', 'No filter options available.'),
    errorLabel: selector.isError
      ? translate('crm.customerDirectory.filters.loadError', 'Could not load options.')
      : undefined,
    onRetry: () => {
      void selector.refetch();
    },
    retryLabel: translate('auth.retry', 'Retry'),
    retryOnOpen: true,
    silentEmpty: true,
  });
  const courseFilterOptions = query.courseSelector.items.map((course) => ({
    id: course.courseRef,
    label: formatCoursePresentation({ course, locale, translate }) ?? fallback,
  }));
  const pageLabels = {
    next: translate('crm.customerLearning.pagination.next', 'Next page'),
    of: (totalPages: number) =>
      translate('crm.customerLearning.pagination.of', 'of {{totalPages}}', { totalPages }),
    page: translate('crm.customerLearning.pagination.page', 'Page'),
    previous: translate('crm.customerLearning.pagination.previous', 'Previous page'),
    showing: (from: number, to: number, total: number) =>
      translate('crm.customerLearning.pagination.showing', 'Showing {{from}}–{{to}} of {{total}}', {
        from,
        to,
        total,
      }),
    showingWithoutTotal: (from: number, to: number) =>
      from === 0
        ? ''
        : translate('crm.customerLearning.pagination.range', 'Showing {{from}}–{{to}}', {
            from,
            to,
          }),
  };
  const paginationCandidate =
    section === 'attempts'
      ? undefined
      : section === 'courses'
        ? query.courseCollection?.meta
        : section === 'registrations'
          ? query.registrationCollection !== undefined && 'meta' in query.registrationCollection
            ? query.registrationCollection.meta
            : undefined
          : query.certificationCollection?.meta;
  const pagination = numberedPageMeta(paginationCandidate);
  const attemptPage = attemptCursor === undefined ? 1 : (attemptCursorHistory?.length ?? 0) + 2;
  const knownAttemptPageCount =
    query.emLogs?.meta.hasNextPage === true ? attemptPage + 1 : attemptPage;
  const knownAttemptPages = Array.from({ length: knownAttemptPageCount }, (_, index) => index + 1);
  const changeAttemptPage = (nextPage: number) => {
    if (nextPage === attemptPage) return;

    const cursorHistory = attemptCursorHistory ?? [];
    if (nextPage === attemptPage + 1) {
      const nextCursor = query.emLogs?.meta.nextCursor;
      if (nextCursor === null || nextCursor === undefined) return;
      onAttemptCursorChange(
        nextCursor,
        attemptCursor === undefined ? cursorHistory : [...cursorHistory, attemptCursor]
      );
      return;
    }

    onAttemptCursorChange(
      nextPage === 1 ? undefined : cursorHistory[nextPage - 2],
      cursorHistory.slice(0, Math.max(0, nextPage - 2))
    );
  };
  const relatedDocumentLabels: RelatedDocumentLabels = {
    documentLabel: (type: string | null, index: number) => {
      if (type === null) {
        return translate('crm.customerLearning.documents.document', 'Document {{number}}', {
          number: index + 1,
        });
      }
      if (!Object.hasOwn(documentTypeLabels, type)) return type;
      const descriptor = documentTypeLabels[type as CrmCustomerDocumentType];
      return translate(descriptor.key, descriptor.fallback);
    },
    emptyLabel: translate('crm.customerLearning.documents.empty', 'No related documents'),
    statusLabel: (status) => {
      if (status === null) return fallback;
      if (!(status in documentStatusLabels)) return status;
      const descriptor = documentStatusLabels[status as CrmCustomerDocumentStatus];
      return translate(descriptor.key, descriptor.fallback);
    },
  };
  const sortableColumn = (label: string, sortColumnKey: LearningSortKey) => {
    const control = (
      <SortableHeaderButton
        label={label}
        sortKey={sortColumnKey}
        currentSortKey={sortKey}
        currentSortDirection={sortDirection}
        onToggle={toggleSort}
        disabled={loadingRows}
      />
    );
    return { header: control, mobileControl: control };
  };

  const courseColumns: readonly ResponsiveTableColumn<Course>[] = [
    {
      key: 'courseType',
      header: translate('crm.customerLearning.fields.courseType', 'Course type'),
      cell: (row) => <OptionalValue fallback={fallback} value={row.type?.name} />,
      size: 'flex',
    },
    {
      key: 'leadInstructor',
      header: translate('crm.customerLearning.fields.leadInstructor', 'Lead instructor'),
      cell: (row) => <OptionalValue fallback={fallback} value={row.leadInstructor?.name} />,
      size: 'flex',
    },
    {
      key: 'status',
      header: translate('crm.customerLearning.fields.status', 'Status'),
      cell: (row) => <CourseStatusBadge fallback={fallback} status={row.status} />,
      size: 'status',
    },
    {
      key: 'date',
      ...sortableColumn(
        translate('crm.customerLearning.fields.courseDate', 'Course date'),
        'courseStartAt'
      ),
      cell: (row) => <OptionalValue fallback={fallback} value={formatDate(row.startAt, locale)} />,
      className: 'whitespace-nowrap',
      size: 'date',
    },
    {
      key: 'school',
      header: translate('crm.customerLearning.fields.school', 'School'),
      cell: (row) => <OptionalValue fallback={fallback} value={row.school?.name} />,
      size: 'flex',
    },
    {
      key: 'number',
      header: translate('crm.customerLearning.fields.courseNumber', 'Course number'),
      cell: (row) => <OptionalValue fallback={fallback} value={row.number} />,
      size: 'status',
    },
    {
      key: 'actions',
      header: translate('crm.customerLearning.fields.actions', 'Actions'),
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (row) => (
        <div className="flex justify-end">
          <LearningRowActions
            label={translate(
              'crm.customerLearning.actions.openMenu',
              'Open actions for {{record}}',
              {
                record: row.type?.name ?? row.number ?? fallback,
              }
            )}
            actions={[
              {
                label: translate(
                  'crm.customerLearning.actions.viewRegistrations',
                  'View registrations'
                ),
                onSelect: () => {
                  onViewCourseRegistrations(row.courseRef);
                },
              },
            ]}
          />
        </div>
      ),
      size: 'action',
    },
  ];

  const registrationColumns: readonly ResponsiveTableColumn<Registration>[] = [
    {
      key: 'registration',
      ...sortableColumn(
        translate('crm.customerLearning.fields.registrationNumber', 'Registration №'),
        'registrationNumber'
      ),
      cell: (registration) => (
        <OptionalValue fallback={fallback} value={registration.registrationNumber} />
      ),
      size: 'compact',
    },
    {
      key: 'status',
      header: translate('crm.customerLearning.fields.paymentStatus', 'Payment status'),
      cell: (registration) => (
        <PaymentStatusBadge fallback={fallback} status={registration.instructorPaymentStatus} />
      ),
      size: 'status',
    },
    {
      key: 'submittedAt',
      ...sortableColumn(
        translate('crm.customerLearning.fields.registrationDate', 'Reg date'),
        'submittedAt'
      ),
      cell: (registration) => (
        <OptionalValue fallback={fallback} value={formatDate(registration.submittedAt, locale)} />
      ),
      className: 'whitespace-nowrap',
      size: 'date',
    },
    {
      key: 'course',
      header: translate('crm.customerLearning.fields.course', 'Course'),
      cell: (registration) => (
        <OptionalValue
          fallback={fallback}
          value={formatCoursePresentation({
            course: registration.course,
            locale,
            translate,
          })}
        />
      ),
      size: 'wide',
    },
    {
      key: 'instructor',
      header: translate(
        'crm.customerLearning.fields.submittingInstructor',
        'Submitting instructor'
      ),
      cell: (registration) => (
        <OptionalValue fallback={fallback} value={registration.instructor?.name} />
      ),
      size: 'flex',
    },
    {
      key: 'school',
      header: translate('crm.customerLearning.fields.submittingSchool', 'Submitting school'),
      cell: (registration) => (
        <OptionalValue fallback={fallback} value={registration.school?.name} />
      ),
      size: 'flex',
    },
    {
      key: 'cost',
      ...sortableColumn(translate('crm.customerLearning.fields.cost', 'Cost'), 'cost'),
      cell: (registration) => (
        <OptionalValue
          fallback={fallback}
          value={formatCurrency(registration.cost, 'USD', locale)}
        />
      ),
      size: 'compact',
    },
    {
      key: 'modules',
      header: translate('crm.customerLearning.fields.modules', 'Modules'),
      cell: (registration) =>
        formatPassedCount({
          passed: registration.modules.completed,
          total: registration.modules.total,
          translate,
        }),
      size: 'status',
    },
    {
      key: 'exams',
      header: translate('crm.customerLearning.fields.exams', 'Exams'),
      cell: (registration) =>
        formatPassedCount({
          ...mainExamPassedCount(registration),
          translate,
        }),
      size: 'status',
    },
    {
      key: 'documents',
      header: translate('crm.customerLearning.fields.documents', 'Documents'),
      cell: (registration) => (
        <LearningRelatedDocuments
          documents={registration.relatedDocuments}
          labels={relatedDocumentLabels}
        />
      ),
      size: 'wide',
    },
    {
      key: 'actions',
      header: translate('crm.customerLearning.fields.actions', 'Actions'),
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (registration) => (
        <div className="flex justify-end">
          <RegistrationActions
            registration={registration}
            translate={translate}
            onViewAttempts={() => {
              onFiltersChange({ registrationId: registration.id });
            }}
            onViewDocuments={() => {
              onViewRegistrationDocuments(registration.id);
            }}
          />
        </div>
      ),
      size: 'action',
    },
  ];

  const certificationColumns: readonly ResponsiveTableColumn<Certification>[] = [
    {
      key: 'registration',
      header: translate('crm.customerLearning.fields.registrationNumberShort', 'Reg №'),
      cell: (certification) => (
        <OptionalValue fallback={fallback} value={certification.registrationNumber} />
      ),
      size: 'flex',
    },
    {
      key: 'certification',
      header: translate('crm.customerLearning.fields.certification', 'Certification'),
      cell: (certification) => (
        <OptionalValue fallback={fallback} value={certification.type?.name} />
      ),
      size: 'wide',
    },
    {
      key: 'course',
      header: translate('crm.customerLearning.fields.course', 'Course'),
      cell: (certification) => (
        <OptionalValue
          fallback={fallback}
          value={formatCoursePresentation({
            course: certification.course,
            locale,
            translate,
          })}
        />
      ),
      size: 'wide',
    },
    {
      key: 'submittedAt',
      header: translate('crm.customerLearning.fields.submittedAt', 'Submitted'),
      cell: (certification) => (
        <OptionalValue fallback={fallback} value={formatDate(certification.submittedAt, locale)} />
      ),
      className: 'whitespace-nowrap',
      size: 'date',
    },
    {
      key: 'examPassedAt',
      header: translate('crm.customerLearning.fields.examPassedAt', 'Date exam passed'),
      cell: (certification) => (
        <OptionalValue fallback={fallback} value={formatDate(certification.examPassedAt, locale)} />
      ),
      className: 'whitespace-nowrap',
      size: 'date',
    },
    {
      key: 'status',
      header: translate('crm.customerLearning.fields.status', 'Status'),
      cell: (certification) => (
        <StatusBadge tone={certification.completed ? 'positive' : 'neutral'}>
          {certification.completed
            ? translate('crm.customerLearning.completed', 'Completed')
            : translate('crm.customerLearning.incomplete', 'Incomplete')}
        </StatusBadge>
      ),
      size: 'status',
    },
    {
      key: 'certifiedAt',
      ...sortableColumn(
        translate('crm.customerLearning.fields.certifiedAt', 'Certified'),
        'certifiedAt'
      ),
      cell: (certification) => (
        <OptionalValue fallback={fallback} value={formatDate(certification.certifiedAt, locale)} />
      ),
      className: 'whitespace-nowrap',
      size: 'date',
    },
    {
      key: 'expiresAt',
      ...sortableColumn(translate('crm.customerLearning.fields.expiresAt', 'Expires'), 'expiresAt'),
      cell: (certification) => (
        <OptionalValue fallback={fallback} value={formatDate(certification.expiresAt, locale)} />
      ),
      className: 'whitespace-nowrap',
      size: 'date',
    },
    {
      key: 'instructor',
      header: translate('crm.customerLearning.fields.instructor', 'Instructor'),
      cell: (certification) => (
        <OptionalValue fallback={fallback} value={certification.instructor?.name} />
      ),
      size: 'status',
    },
    {
      key: 'documents',
      header: translate('crm.customerLearning.fields.documents', 'Documents'),
      cell: (certification) => (
        <LearningRelatedDocuments
          documents={certification.relatedDocuments}
          labels={relatedDocumentLabels}
        />
      ),
      size: 'wide',
    },
    {
      key: 'actions',
      header: translate('crm.customerLearning.fields.actions', 'Actions'),
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (certification) => (
        <div className="flex justify-end">
          <LearningRowActions
            label={translate(
              'crm.customerLearning.actions.openMenu',
              'Open actions for {{record}}',
              {
                record:
                  certification.type?.name ??
                  certification.course?.name ??
                  certification.registrationNumber ??
                  fallback,
              }
            )}
            actions={[
              ...(certification.registrationNumber
                ? [
                    {
                      label: translate(
                        'crm.customerLearning.actions.viewRegistration',
                        'View registration'
                      ),
                      onSelect: () => {
                        onViewRegistration(certification.registrationNumber ?? '');
                      },
                    },
                  ]
                : []),
              {
                ...(certification.certificatePreview.state === 'ready'
                  ? {
                      onSelect: () => {
                        openCertificatePreview(certification);
                      },
                    }
                  : {
                      description: translate(
                        certification.certificatePreview.reason === 'processing'
                          ? 'crm.customerLearning.actions.certificateProcessing'
                          : 'crm.customerLearning.actions.certificateMissing',
                        certification.certificatePreview.reason === 'processing'
                          ? 'Certification Card is still processing.'
                          : 'Certification Card is not available.'
                      ),
                      disabled: true,
                    }),
                label: translate(
                  'crm.customerLearning.actions.viewCertificate',
                  'Open certification card'
                ),
              },
            ]}
          />
        </div>
      ),
      size: 'action',
    },
  ];

  const emLogColumns: readonly ResponsiveTableColumn<EmLog>[] = [
    {
      key: 'learningItem',
      header: translate('crm.customerLearning.fields.moduleExam', 'Module / exam'),
      cell: (log) => <OptionalValue fallback={fallback} value={log.module?.title} />,
      size: 'flex',
    },
    {
      key: 'result',
      ...sortableColumn(
        translate('crm.customerLearning.filters.result', 'Result'),
        'attemptResult'
      ),
      cell: (log) => <ResultBadge fallback={fallback} result={log.result} />,
      size: 'status',
    },
    {
      key: 'type',
      header: translate('crm.customerLearning.fields.type', 'Type'),
      cell: (log) => <OptionalValue fallback={fallback} value={log.type} />,
      size: 'status',
    },
    {
      key: 'attemptDate',
      ...sortableColumn(
        translate('crm.customerLearning.fields.attemptDate', 'Attempt date'),
        'attemptDate'
      ),
      cell: (log) => (
        <OptionalValue fallback={fallback} value={formatDate(log.attempted_at, locale)} />
      ),
      className: 'whitespace-nowrap',
      size: 'date',
    },
    {
      key: 'passedDate',
      header: translate('crm.customerLearning.fields.passedDate', 'Passed date'),
      cell: (log) => (
        <OptionalValue fallback={fallback} value={formatDate(log.passed_at, locale)} />
      ),
      className: 'whitespace-nowrap',
      size: 'date',
    },
    {
      key: 'score',
      ...sortableColumn(
        translate('crm.customerLearning.fields.score', 'Score (points)'),
        'attemptScore'
      ),
      cell: (log) => <EmLogScore log={log} fallback={fallback} locale={locale} />,
      size: 'status',
    },
    {
      key: 'documents',
      header: translate('crm.customerLearning.fields.documents', 'Documents'),
      cell: (log) => (
        <EmLogDocumentCount
          documentCount={log.document_count}
          fallback={fallback}
          locale={locale}
        />
      ),
      size: 'status',
    },
    {
      key: 'instructor',
      header: translate('crm.customerLearning.fields.instructor', 'Instructor'),
      cell: (log) => <OptionalValue fallback={fallback} value={log.instructor?.name} />,
      size: 'flex',
    },
  ];

  return (
    <section
      aria-labelledby="customer-learning-heading"
      className="flex min-w-0 flex-col gap-4"
      data-testid="crm-customer-learning"
    >
      <Heading as="h2" id="customer-learning-heading" className="sr-only">
        {translate('crm.customerLearning.title', 'Learning')}
      </Heading>
      {certificatePreview &&
      certificatePreview.customerId === customerId &&
      certificatePreview.viewerAccessScope === viewerAccessScope &&
      section === 'certifications' ? (
        <CertificationCardPreview
          key={`${viewerAccessScope}:${certificatePreview.href}`}
          href={certificatePreview.href}
          viewerAccessScope={viewerAccessScope}
          onClose={() => {
            setCertificatePreview(null);
          }}
        />
      ) : null}

      <LearningSectionTabs
        onSectionChange={onSectionChange}
        section={section}
        translate={translate}
      />

      {query.isError ? (
        <Alert variant="destructive">
          <AlertTitle>
            {queryTimedOut && (q?.length ?? 0) > 0
              ? translate('crm.customerLearning.searchTimeoutTitle', 'Search took too long')
              : queryTimedOut
                ? translate('crm.customerLearning.timeoutTitle', 'Learning took too long')
                : translate('crm.customerLearning.unavailableTitle', 'Learning unavailable')}
          </AlertTitle>
          <AlertDescription className="space-y-3">
            <p>
              {queryTimedOut && (q?.length ?? 0) > 0
                ? translate(
                    'crm.customerLearning.searchTimeoutDescription',
                    'Previous results are still shown. Narrow the search or retry.'
                  )
                : queryTimedOut
                  ? translate(
                      'crm.customerLearning.timeoutWithPreviousDescription',
                      'Previous Learning results are still shown. Retry when ready.'
                    )
                  : translate(
                      'crm.customerLearning.unavailableWithPreviousDescription',
                      'Previous Learning results are still shown. Retry when ready.'
                    )}
            </p>
            {queryErrorStatus !== 403 && queryErrorStatus !== 404 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void query.refetch()}
              >
                {translate('common.retry', 'Retry')}
              </Button>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {hasLoadedData || hasActiveQuery ? (
        <SearchFilterToolbar>
          <SearchInput
            className="min-w-0 max-w-lg flex-1"
            aria-label={translate(`crm.customerLearning.search.${section}`, 'Search')}
            placeholder={translate(`crm.customerLearning.search.${section}`, 'Search')}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            onCompositionStart={onCompositionStart}
            onCompositionEnd={(event) => {
              onCompositionEnd(event.currentTarget.value);
            }}
          />
          <FilterPopover
            activeFilterCount={activeFilterCount}
            clearLabel={translate('crm.customerLearning.filters.clear', 'Clear filters')}
            disabled={loadingRows}
            label={translate('crm.customerLearning.filters.trigger', 'Filters')}
            onClear={() => {
              onFiltersChange({});
            }}
            onOpenChange={setFilterControlsOpen}
            testId="crm-customer-learning-filters-popover"
          >
            <FilterPopoverFields>
              {section === 'attempts' ? (
                <>
                  <FilterPopoverSelect
                    allLabel={allLabel}
                    id="learning-result-filter"
                    {...selectorState(query.selectors.emLogResults)}
                    label={translate('crm.customerLearning.filters.result', 'Result')}
                    value={filters.result}
                    onChange={(result) => {
                      onFiltersChange({ ...filters, result });
                    }}
                    options={emLogFilterValues.results.map((result) => ({
                      label: getLearningValueLabel(
                        'result',
                        result.dbValue,
                        translate,
                        result.display
                      ),
                      value: result.dbValue,
                    }))}
                  />
                  <FilterPopoverSelect
                    allLabel={allLabel}
                    id="learning-type-filter"
                    {...selectorState(query.selectors.emLogTypes)}
                    label={translate('crm.customerLearning.filters.type', 'Type')}
                    value={filters.type}
                    onChange={(type) => {
                      onFiltersChange({ ...filters, type });
                    }}
                    options={emLogFilterValues.types.map((type) => ({
                      label: type.display,
                      value: type.dbValue,
                    }))}
                  />
                  <FilterPopoverInput
                    id="learning-attempt-date-from"
                    label={translate(
                      'crm.customerLearning.filters.attemptedFrom',
                      'Attempt date from'
                    )}
                    type="date"
                    max={filters.attemptedTo}
                    value={filters.attemptedFrom}
                    onChange={(attemptedFrom) => {
                      onFiltersChange({ ...filters, attemptedFrom });
                    }}
                  />
                  <FilterPopoverInput
                    id="learning-attempt-date-to"
                    label={translate('crm.customerLearning.filters.attemptedTo', 'Attempt date to')}
                    type="date"
                    min={filters.attemptedFrom}
                    value={filters.attemptedTo}
                    onChange={(attemptedTo) => {
                      onFiltersChange({ ...filters, attemptedTo });
                    }}
                  />
                </>
              ) : null}
              {section !== 'certifications' ? (
                <FilterPopoverSelect
                  allLabel={allLabel}
                  id="learning-course-type-filter"
                  label={translate('crm.customerLearning.filters.courseType', 'Course type')}
                  value={filters.courseTypeId}
                  onChange={(courseTypeId) => {
                    onFiltersChange({ ...filters, courseTypeId });
                  }}
                  {...selectorState(query.selectors.courseTypes)}
                  options={(query.courseTypes?.data.courseTypes ?? []).map((courseType) => ({
                    label: courseType.label,
                    value: courseType.id,
                  }))}
                />
              ) : (
                <FilterPopoverSelect
                  allLabel={allLabel}
                  id="learning-certification-type-filter"
                  {...selectorState(query.selectors.certificationTypes)}
                  label={translate(
                    'crm.customerLearning.filters.certificationType',
                    'Certification type'
                  )}
                  value={filters.certificationTypeId}
                  onChange={(certificationTypeId) => {
                    onFiltersChange({ ...filters, certificationTypeId });
                  }}
                  options={(query.certificationTypes?.data.certificationTypes ?? []).map(
                    (type) => ({
                      label: type.label,
                      value: type.id,
                    })
                  )}
                />
              )}
              <FilterPopoverSelect
                allLabel={allLabel}
                id="learning-instructor-filter"
                label={
                  section === 'certifications'
                    ? translate(
                        'crm.customerLearning.filters.certifyingInstructor',
                        'Certifying instructor'
                      )
                    : section === 'courses'
                      ? translate('crm.customerLearning.filters.leadInstructor', 'Lead instructor')
                      : translate(
                          'crm.customerLearning.filters.instructor',
                          'Submitting instructor'
                        )
                }
                value={filters.instructorId}
                onChange={(instructorId) => {
                  onFiltersChange({ ...filters, instructorId });
                }}
                {...selectorState(query.instructorProfiles)}
                onRetry={query.instructorProfiles.retry}
                loadingMore={query.instructorProfiles.isFetchingNextPage}
                loadingMoreLabel={translate('common.loading', 'Loading...')}
                hasNextPage={query.instructorProfiles.hasNextPage}
                loadedPageCount={query.instructorProfiles.data?.pages.length}
                paginationScopeKey={JSON.stringify([
                  viewerAccessScope,
                  customerId,
                  instructorQuery,
                ])}
                onListEndReached={query.instructorProfiles.loadNextPage}
                onSearchValueChange={setInstructorQuery}
                searchPlaceholder={translate(
                  'crm.customerLearning.filters.searchInstructors',
                  'Search instructors'
                )}
                searchValue={instructorQuery}
                showOptionsWithoutSearch
                options={query.instructorProfiles.items.map((profile) => ({
                  label: profile.user.displayName,
                  value: profile.id,
                }))}
              />
              {section === 'courses' || section === 'registrations' ? (
                <FilterPopoverSelect
                  allLabel={allLabel}
                  id="learning-school-filter"
                  label={translate('crm.customerLearning.filters.school', 'Associated school')}
                  value={filters.schoolId}
                  onChange={(schoolId) => {
                    onFiltersChange({ ...filters, schoolId });
                  }}
                  {...selectorState(query.selectors.organizations)}
                  options={(query.organizations?.data.organizations ?? []).map((organization) => ({
                    label: organization.name,
                    value: organization.id,
                  }))}
                />
              ) : null}
              {section === 'courses' ? (
                <>
                  <FilterPopoverSelect
                    allLabel={allLabel}
                    id="learning-course-status-filter"
                    label={translate('crm.customerLearning.filters.courseStatus', 'Course status')}
                    value={filters.courseStatus}
                    onChange={(courseStatus) => {
                      onFiltersChange({ ...filters, courseStatus });
                    }}
                    {...selectorState(query.selectors.courseStatuses)}
                    options={(query.courseStatuses?.data.courseStatuses ?? []).map((status) => ({
                      label: getLearningValueLabel(
                        'course',
                        status.dbValue,
                        translate,
                        status.display
                      ),
                      value: status.dbValue,
                    }))}
                  />
                  <FilterPopoverInput
                    id="learning-course-date-from"
                    label={translate(
                      'crm.customerLearning.filters.courseDateFrom',
                      'Course date from'
                    )}
                    type="date"
                    value={filters.dateFrom}
                    onChange={(dateFrom) => {
                      onFiltersChange({ ...filters, dateFrom });
                    }}
                  />
                  <FilterPopoverInput
                    id="learning-course-date-to"
                    label={translate('crm.customerLearning.filters.courseDateTo', 'Course date to')}
                    type="date"
                    value={filters.dateTo}
                    onChange={(dateTo) => {
                      onFiltersChange({ ...filters, dateTo });
                    }}
                  />
                </>
              ) : null}
              {section === 'registrations' ? (
                <>
                  <FilterPopoverSelect
                    allLabel={allLabel}
                    id="learning-course-filter"
                    label={translate('crm.customerLearning.filters.course', 'Course')}
                    value={filters.courseId}
                    onChange={(courseId) => {
                      onFiltersChange({ ...filters, courseId });
                    }}
                    {...selectorState(query.courseSelector)}
                    onRetry={query.courseSelector.retry}
                    loadingMore={query.courseSelector.isFetchingNextPage}
                    loadingMoreLabel={translate('common.loading', 'Loading...')}
                    hasNextPage={query.courseSelector.hasNextPage}
                    loadedPageCount={query.courseSelector.data?.pages.length}
                    paginationScopeKey={JSON.stringify([
                      viewerAccessScope,
                      customerId,
                      courseQuery,
                    ])}
                    onListEndReached={query.courseSelector.loadNextPage}
                    onSearchValueChange={setCourseQuery}
                    searchPlaceholder={translate(
                      'crm.customerLearning.filters.searchCourses',
                      'Search courses'
                    )}
                    searchValue={courseQuery}
                    showOptionsWithoutSearch
                    options={courseFilterOptions.map((option) => ({
                      label: option.label,
                      value: option.id,
                    }))}
                  />
                  <FilterPopoverSelect
                    allLabel={allLabel}
                    id="learning-instructor-payment-filter"
                    label={translate(
                      'crm.customerLearning.filters.instructorPaymentStatus',
                      'Instructor payment status'
                    )}
                    value={filters.instructorPaymentStatus}
                    onChange={(instructorPaymentStatus) => {
                      onFiltersChange({ ...filters, instructorPaymentStatus });
                    }}
                    {...selectorState(query.selectors.registrationPaymentStatuses)}
                    options={(query.registrationPaymentStatuses?.data.paymentStatuses ?? []).map(
                      (value) => ({
                        label: getLearningValueLabel(
                          'registration',
                          value.dbValue,
                          translate,
                          value.display
                        ),
                        value: value.dbValue,
                      })
                    )}
                  />
                  <FilterPopoverSelect
                    allLabel={allLabel}
                    id="learning-student-payment-filter"
                    label={translate(
                      'crm.customerLearning.filters.studentPaymentStatus',
                      'Student payment status'
                    )}
                    value={filters.studentPaymentStatus}
                    onChange={(studentPaymentStatus) => {
                      onFiltersChange({ ...filters, studentPaymentStatus });
                    }}
                    {...selectorState(query.selectors.registrationPaymentStatuses)}
                    options={(query.registrationPaymentStatuses?.data.paymentStatuses ?? []).map(
                      (value) => ({
                        label: getLearningValueLabel(
                          'registration',
                          value.dbValue,
                          translate,
                          value.display
                        ),
                        value: value.dbValue,
                      })
                    )}
                  />
                  <FilterPopoverSelect
                    allLabel={allLabel}
                    id="learning-registration-type-filter"
                    label={translate(
                      'crm.customerLearning.filters.registrationType',
                      'Registration type'
                    )}
                    value={filters.registrationType}
                    onChange={(registrationType) => {
                      onFiltersChange({ ...filters, registrationType });
                    }}
                    {...selectorState(query.selectors.registrationTypes)}
                    options={(query.registrationTypes?.data.registrationTypes ?? []).map(
                      (value) => ({
                        label: value.display,
                        value: value.dbValue,
                      })
                    )}
                  />
                  <FilterPopoverSelect
                    allLabel={allLabel}
                    id="learning-registration-reference-filter"
                    label={translate(
                      'crm.customerLearning.filters.registrationReferenceType',
                      'Registration reference type'
                    )}
                    value={filters.registrationReferenceType}
                    onChange={(registrationReferenceType) => {
                      onFiltersChange({ ...filters, registrationReferenceType });
                    }}
                    {...selectorState(query.selectors.registrationReferenceTypes)}
                    options={(query.registrationReferenceTypes?.data.referenceTypes ?? []).map(
                      (value) => ({ label: value.display, value: value.dbValue })
                    )}
                  />
                  <FilterPopoverInput
                    id="learning-cost-min"
                    label={translate('crm.customerLearning.filters.costMin', 'Minimum cost')}
                    type="number"
                    min="0"
                    step="0.01"
                    value={filters.costMin === undefined ? undefined : String(filters.costMin)}
                    onChange={(value) => {
                      onFiltersChange({
                        ...filters,
                        costMin: value === undefined ? undefined : Number(value),
                      });
                    }}
                  />
                  <FilterPopoverInput
                    id="learning-cost-max"
                    label={translate('crm.customerLearning.filters.costMax', 'Maximum cost')}
                    type="number"
                    min="0"
                    step="0.01"
                    value={filters.costMax === undefined ? undefined : String(filters.costMax)}
                    onChange={(value) => {
                      onFiltersChange({
                        ...filters,
                        costMax: value === undefined ? undefined : Number(value),
                      });
                    }}
                  />
                  <FilterPopoverInput
                    id="learning-submitted-from"
                    label={translate(
                      'crm.customerLearning.filters.submittedFrom',
                      'Submitted from'
                    )}
                    type="date"
                    value={filters.submittedFrom}
                    onChange={(submittedFrom) => {
                      onFiltersChange({ ...filters, submittedFrom });
                    }}
                  />
                  <FilterPopoverInput
                    id="learning-submitted-to"
                    label={translate('crm.customerLearning.filters.submittedTo', 'Submitted to')}
                    type="date"
                    value={filters.submittedTo}
                    onChange={(submittedTo) => {
                      onFiltersChange({ ...filters, submittedTo });
                    }}
                  />
                </>
              ) : null}
              {section === 'registrations' || section === 'certifications' ? (
                <>
                  <FilterPopoverInput
                    id="learning-certified-from"
                    label={translate(
                      'crm.customerLearning.filters.certifiedFrom',
                      'Certified from'
                    )}
                    type="date"
                    value={filters.certifiedFrom}
                    onChange={(certifiedFrom) => {
                      onFiltersChange({ ...filters, certifiedFrom });
                    }}
                  />
                  <FilterPopoverInput
                    id="learning-certified-to"
                    label={translate('crm.customerLearning.filters.certifiedTo', 'Certified to')}
                    type="date"
                    value={filters.certifiedTo}
                    onChange={(certifiedTo) => {
                      onFiltersChange({ ...filters, certifiedTo });
                    }}
                  />
                </>
              ) : null}
              {section === 'attempts' ? (
                <div className="space-y-1">
                  <FilterPopoverSelect
                    allLabel={allLabel}
                    {...selectorState(query.emLogRegistrationSelector)}
                    onRetry={query.emLogRegistrationSelector.retry}
                    hasNextPage={query.emLogRegistrationSelector.hasNextPage}
                    loadedPageCount={query.emLogRegistrationSelector.data?.pages.length}
                    paginationScopeKey={JSON.stringify([
                      viewerAccessScope,
                      customerId,
                      registrationQuery,
                    ])}
                    emptyText={translate(
                      'crm.customerLearning.filters.registrationEmpty',
                      'No registrations match your search.'
                    )}
                    id="learning-registration-filter"
                    label={translate('crm.customerLearning.filters.registration', 'Registration')}
                    loading={query.emLogRegistrationSelector.isLoading}
                    loadingLabel={translate('common.loading', 'Loading')}
                    loadingMore={query.emLogRegistrationSelector.isFetchingNextPage}
                    loadingMoreLabel={translate('common.loading', 'Loading')}
                    onListEndReached={query.emLogRegistrationSelector.loadNextPage}
                    onSearchValueChange={setRegistrationQuery}
                    searchPlaceholder={translate(
                      'crm.customerLearning.filters.registrationSearch',
                      'Search registrations'
                    )}
                    searchValue={registrationQuery}
                    showOptionsWithoutSearch
                    value={filters.registrationId}
                    onChange={(registrationId) => {
                      onFiltersChange({ ...filters, learningItemId: undefined, registrationId });
                    }}
                    options={query.emLogRegistrationSelector.items.map((registration) => ({
                      label: registration.registrationNumber ?? fallback,
                      value: registration.id,
                    }))}
                  />
                </div>
              ) : null}
            </FilterPopoverFields>
          </FilterPopover>
        </SearchFilterToolbar>
      ) : null}

      {section === 'courses' ? (
        <ResponsiveTable
          ariaLabel={translate('crm.customerLearning.tables.courses', 'Customer courses')}
          mobileControlsLabel={translate('crm.customerLearning.fields.sort', 'Sort')}
          rows={courses}
          columns={courseColumns}
          breakpointClassName="xl"
          columnLayout="stable"
          isLoading={loadingRows}
          loadingLabel={translate('crm.customerLearning.loading', 'Loading Learning')}
          loadingRowCount={pagination?.pageSize}
          emptyState={emptyState}
          getRowKey={(row) => row.courseRef}
          getRowNavigation={(row) => ({
            ariaLabel: translate(
              'crm.customerLearning.actions.viewCourseProfile',
              'Open Course profile'
            ),
            onNavigate: () => {
              onViewCourseProfile(row.courseRef);
            },
          })}
          renderMobileCard={(row) => (
            <CourseCard
              row={row}
              fallback={fallback}
              locale={locale}
              translate={translate}
              onViewRegistrations={onViewCourseRegistrations}
            />
          )}
        />
      ) : section === 'registrations' ? (
        <ResponsiveTable
          ariaLabel={translate(
            'crm.customerLearning.tables.registrations',
            'Customer registrations'
          )}
          mobileControlsLabel={translate('crm.customerLearning.fields.sort', 'Sort')}
          rows={registrations}
          columns={registrationColumns}
          breakpointClassName="xl"
          isLoading={loadingRows}
          loadingLabel={translate('crm.customerLearning.loading', 'Loading Learning')}
          loadingRowCount={pagination?.pageSize}
          emptyState={emptyState}
          getRowKey={(registration) => registration.id}
          getRowNavigation={(registration) => ({
            ariaLabel: translate(
              'crm.customerLearning.actions.openRegistrationProfile',
              'Open registration profile'
            ),
            onNavigate: () => {
              onOpenRegistrationProfile(registration.id);
            },
          })}
          renderMobileCard={(registration) => (
            <RegistrationCard
              registration={registration}
              fallback={fallback}
              locale={locale}
              onViewDocuments={() => {
                onViewRegistrationDocuments(registration.id);
              }}
              relatedDocumentLabels={relatedDocumentLabels}
              translate={translate}
              onViewAttempts={(learningItemId) => {
                onFiltersChange({ learningItemId, registrationId: registration.id });
              }}
            />
          )}
        />
      ) : section === 'certifications' ? (
        <ResponsiveTable
          ariaLabel={translate(
            'crm.customerLearning.tables.certifications',
            'Customer certification history'
          )}
          mobileControlsLabel={translate('crm.customerLearning.fields.sort', 'Sort')}
          rows={certifications}
          columns={certificationColumns}
          breakpointClassName="xl"
          isLoading={loadingRows}
          loadingLabel={translate('crm.customerLearning.loading', 'Loading Learning')}
          loadingRowCount={pagination?.pageSize}
          emptyState={emptyState}
          getRowKey={(certification) => certification.id}
          getRowNavigation={(certification) => ({
            ariaLabel: translate(
              'crm.customerLearning.actions.openCertificationProfile',
              'Open certification profile'
            ),
            onNavigate: () => {
              onOpenCertificationProfile(certification.id);
            },
          })}
          renderMobileCard={(certification) => (
            <CertificationCard
              certification={certification}
              onOpenCertificate={openCertificatePreview}
              fallback={fallback}
              locale={locale}
              onViewRegistration={onViewRegistration}
              relatedDocumentLabels={relatedDocumentLabels}
              translate={translate}
            />
          )}
        />
      ) : (
        <ResponsiveTable
          ariaLabel={translate(
            'crm.customerLearning.tables.attempts',
            'Customer exam and module attempts'
          )}
          mobileControlsLabel={translate('crm.customerLearning.fields.sort', 'Sort')}
          rows={[...emLogs]}
          columns={emLogColumns}
          breakpointClassName="xl"
          isLoading={loadingRows}
          loadingLabel={translate('crm.customerLearning.loading', 'Loading Learning')}
          loadingRowCount={pagination?.pageSize}
          emptyState={emptyState}
          getRowKey={(log) => log.id}
          getRowNavigation={(log) => ({
            ariaLabel: translate(
              'crm.customerLearning.actions.openEducationModuleLogProfile',
              'Open educational module log profile'
            ),
            onNavigate: () => {
              onOpenEducationModuleLogProfile(log.id);
            },
          })}
          renderMobileCard={(log) => (
            <EmLogCard log={log} fallback={fallback} locale={locale} translate={translate} />
          )}
        />
      )}

      {pagination !== undefined && pagination.totalItems > 0 ? (
        <PaginationControls
          currentPage={pagination.page}
          disabled={loadingRows}
          pageSize={pagination.pageSize}
          totalItems={pagination.totalItems}
          labels={{
            next: pageLabels.next,
            of: pageLabels.of,
            page: pageLabels.page,
            previous: pageLabels.previous,
            showing: pageLabels.showing,
          }}
          onPageChange={onPageChange}
        />
      ) : null}
      {section === 'attempts' &&
      query.emLogs !== undefined &&
      (emLogs.length > 0 || attemptCursor !== undefined || query.emLogs.meta.hasNextPage) ? (
        <PaginationControls
          availablePages={knownAttemptPages}
          currentPage={attemptPage}
          disabled={loadingRows}
          labels={{
            next: pageLabels.next,
            of: pageLabels.of,
            page: pageLabels.page,
            previous: pageLabels.previous,
            showing: pageLabels.showing,
          }}
          pageSize={crmCustomerLearningEmLogsPageSize}
          totalItems={query.emLogs.meta.totalItems}
          onPageChange={changeAttemptPage}
        />
      ) : null}
    </section>
  );
}

function CourseCard({
  fallback,
  locale,
  onViewRegistrations,
  row,
  translate,
}: {
  fallback: string;
  locale: string;
  onViewRegistrations: (courseId: string) => void;
  row: Course;
  translate: Translate;
}) {
  return (
    <Card interactive>
      <CardContent className="space-y-3 p-4">
        <CardHeaderRow
          controls={
            <div className="pointer-events-auto flex items-center gap-2">
              <CourseStatusBadge fallback={fallback} status={row.status} />
              <LearningRowActions
                label={translate(
                  'crm.customerLearning.actions.openMenu',
                  'Open actions for {{record}}',
                  { record: row.type?.name ?? row.number ?? fallback }
                )}
                actions={[
                  {
                    label: translate(
                      'crm.customerLearning.actions.viewRegistrations',
                      'View registrations'
                    ),
                    onSelect: () => {
                      onViewRegistrations(row.courseRef);
                    },
                  },
                ]}
              />
            </div>
          }
        >
          <Heading as="h3" size="sm" weight="semibold" className="min-w-0 flex-1">
            {row.type?.name ?? fallback}
          </Heading>
        </CardHeaderRow>
        <DescriptionList
          columns={1}
          mobileLayout="inline"
          items={[
            {
              id: `${row.courseRef}-instructor`,
              term: translate('crm.customerLearning.fields.leadInstructor', 'Lead instructor'),
              description: <OptionalValue fallback={fallback} value={row.leadInstructor?.name} />,
            },
            {
              id: `${row.courseRef}-date`,
              term: translate('crm.customerLearning.fields.courseDate', 'Course date'),
              description: (
                <OptionalValue fallback={fallback} value={formatDate(row.startAt, locale)} />
              ),
            },
            {
              id: `${row.courseRef}-school`,
              term: translate('crm.customerLearning.fields.school', 'School'),
              description: <OptionalValue fallback={fallback} value={row.school?.name} />,
            },
            {
              id: `${row.courseRef}-number`,
              term: translate('crm.customerLearning.fields.courseNumber', 'Course number'),
              description: <OptionalValue fallback={fallback} value={row.number} />,
            },
          ]}
        />
      </CardContent>
    </Card>
  );
}

function RegistrationCard({
  registration,
  fallback,
  locale,
  onViewAttempts,
  onViewDocuments,
  relatedDocumentLabels,
  translate,
}: {
  registration: Registration;
  fallback: string;
  locale: string;
  onViewAttempts: (learningItemId?: string) => void;
  onViewDocuments: () => void;
  relatedDocumentLabels: RelatedDocumentLabels;
  translate: Translate;
}) {
  return (
    <Card interactive role="article">
      <CardContent className="space-y-3 p-4">
        <CardHeaderRow
          controls={
            <>
              <PaymentStatusBadge
                fallback={fallback}
                status={registration.instructorPaymentStatus}
              />
              <RegistrationActions
                registration={registration}
                onViewAttempts={() => {
                  onViewAttempts();
                }}
                onViewDocuments={onViewDocuments}
                translate={translate}
              />
            </>
          }
        >
          <Heading as="h3" size="sm" weight="semibold" className="min-w-0 flex-1">
            {formatCoursePresentation({ course: registration.course, locale, translate }) ??
              fallback}
          </Heading>
        </CardHeaderRow>
        <DescriptionList
          columns={1}
          mobileLayout="inline"
          items={[
            {
              id: `${registration.id}-number`,
              term: translate('crm.customerLearning.fields.registration', 'Registration'),
              description: (
                <OptionalValue fallback={fallback} value={registration.registrationNumber} />
              ),
            },
            {
              id: `${registration.id}-submitted`,
              term: translate('crm.customerLearning.fields.registrationDate', 'Reg date'),
              description: (
                <OptionalValue
                  fallback={fallback}
                  value={formatDate(registration.submittedAt, locale)}
                />
              ),
            },
            {
              id: `${registration.id}-instructor`,
              term: translate(
                'crm.customerLearning.fields.submittingInstructor',
                'Submitting instructor'
              ),
              description: (
                <OptionalValue fallback={fallback} value={registration.instructor?.name} />
              ),
            },
            {
              id: `${registration.id}-school`,
              term: translate('crm.customerLearning.fields.submittingSchool', 'Submitting school'),
              description: <OptionalValue fallback={fallback} value={registration.school?.name} />,
            },
            {
              id: `${registration.id}-cost`,
              term: translate('crm.customerLearning.fields.cost', 'Cost'),
              description: (
                <OptionalValue
                  fallback={fallback}
                  value={formatCurrency(registration.cost, 'USD', locale)}
                />
              ),
            },
            {
              id: `${registration.id}-modules`,
              term: translate('crm.customerLearning.fields.modules', 'Modules'),
              description: formatPassedCount({
                passed: registration.modules.completed,
                total: registration.modules.total,
                translate,
              }),
            },
            {
              id: `${registration.id}-exams`,
              term: translate('crm.customerLearning.fields.exams', 'Exams'),
              description: formatPassedCount({
                ...mainExamPassedCount(registration),
                translate,
              }),
            },
            {
              id: `${registration.id}-documents`,
              term: translate('crm.customerLearning.fields.documents', 'Documents'),
              description: (
                <LearningRelatedDocuments
                  documents={registration.relatedDocuments}
                  labels={relatedDocumentLabels}
                />
              ),
            },
          ]}
        />
      </CardContent>
    </Card>
  );
}

function CertificationCard({
  certification,
  onOpenCertificate,
  fallback,
  locale,
  onViewRegistration,
  relatedDocumentLabels,
  translate,
}: {
  certification: Certification;
  onOpenCertificate: (certification: Certification) => void;
  fallback: string;
  locale: string;
  onViewRegistration: (registrationNumber: string) => void;
  relatedDocumentLabels: RelatedDocumentLabels;
  translate: Translate;
}) {
  return (
    <Card interactive role="article">
      <CardContent className="space-y-3 p-4">
        <CardHeaderRow
          controls={
            <>
              <StatusBadge tone={certification.completed ? 'positive' : 'neutral'}>
                {certification.completed
                  ? translate('crm.customerLearning.completed', 'Completed')
                  : translate('crm.customerLearning.incomplete', 'Incomplete')}
              </StatusBadge>
              <LearningRowActions
                label={translate(
                  'crm.customerLearning.actions.openMenu',
                  'Open actions for {{record}}',
                  {
                    record:
                      certification.type?.name ??
                      certification.course?.name ??
                      certification.registrationNumber ??
                      fallback,
                  }
                )}
                actions={[
                  ...(certification.registrationNumber
                    ? [
                        {
                          label: translate(
                            'crm.customerLearning.actions.viewRegistration',
                            'View registration'
                          ),
                          onSelect: () => {
                            onViewRegistration(certification.registrationNumber ?? '');
                          },
                        },
                      ]
                    : []),
                  {
                    ...(certification.certificatePreview.state === 'ready'
                      ? {
                          onSelect: () => {
                            onOpenCertificate(certification);
                          },
                        }
                      : {
                          description: translate(
                            certification.certificatePreview.reason === 'processing'
                              ? 'crm.customerLearning.actions.certificateProcessing'
                              : 'crm.customerLearning.actions.certificateMissing',
                            certification.certificatePreview.reason === 'processing'
                              ? 'Certification Card is still processing.'
                              : 'Certification Card is not available.'
                          ),
                          disabled: true,
                        }),
                    label: translate(
                      'crm.customerLearning.actions.viewCertificate',
                      'Open certification card'
                    ),
                  },
                ]}
              />
            </>
          }
        >
          <Heading as="h3" size="sm" weight="semibold" className="min-w-0 flex-1">
            {certification.type?.name ?? fallback}
          </Heading>
        </CardHeaderRow>
        <DescriptionList
          columns={1}
          mobileLayout="inline"
          items={[
            {
              id: `${certification.id}-registration`,
              term: translate('crm.customerLearning.fields.registrationNumberShort', 'Reg №'),
              description: (
                <OptionalValue fallback={fallback} value={certification.registrationNumber} />
              ),
            },
            {
              id: `${certification.id}-submitted`,
              term: translate('crm.customerLearning.fields.submittedAt', 'Submitted'),
              description: (
                <OptionalValue
                  fallback={fallback}
                  value={formatDate(certification.submittedAt, locale)}
                />
              ),
            },
            {
              id: `${certification.id}-exam-passed`,
              term: translate('crm.customerLearning.fields.examPassedAt', 'Date exam passed'),
              description: (
                <OptionalValue
                  fallback={fallback}
                  value={formatDate(certification.examPassedAt, locale)}
                />
              ),
            },
            {
              id: `${certification.id}-course`,
              term: translate('crm.customerLearning.fields.course', 'Course'),
              description: (
                <OptionalValue
                  fallback={fallback}
                  value={formatCoursePresentation({
                    course: certification.course,
                    locale,
                    translate,
                  })}
                />
              ),
            },
            {
              id: `${certification.id}-certified`,
              term: translate('crm.customerLearning.fields.certifiedAt', 'Certified'),
              description: (
                <OptionalValue
                  fallback={fallback}
                  value={formatDate(certification.certifiedAt, locale)}
                />
              ),
            },
            {
              id: `${certification.id}-expires`,
              term: translate('crm.customerLearning.fields.expiresAt', 'Expires'),
              description: (
                <OptionalValue
                  fallback={fallback}
                  value={formatDate(certification.expiresAt, locale)}
                />
              ),
            },
            {
              id: `${certification.id}-instructor`,
              term: translate('crm.customerLearning.fields.instructor', 'Instructor'),
              description: (
                <OptionalValue fallback={fallback} value={certification.instructor?.name} />
              ),
            },
            {
              id: `${certification.id}-documents`,
              term: translate('crm.customerLearning.fields.documents', 'Documents'),
              description: (
                <LearningRelatedDocuments
                  documents={certification.relatedDocuments}
                  labels={relatedDocumentLabels}
                />
              ),
            },
          ]}
        />
      </CardContent>
    </Card>
  );
}

function EmLogCard({
  log,
  fallback,
  locale,
  translate,
}: {
  log: EmLog;
  fallback: string;
  locale: string;
  translate: Translate;
}) {
  return (
    <Card interactive role="article">
      <CardContent className="space-y-3 p-4">
        <CardHeaderRow controls={<ResultBadge fallback={fallback} result={log.result} />}>
          <Heading as="h3" size="sm" weight="semibold" className="min-w-0 flex-1">
            {log.module?.title ?? fallback}
          </Heading>
        </CardHeaderRow>
        <DescriptionList
          columns={1}
          mobileLayout="inline"
          items={[
            {
              id: `${log.id}-type`,
              term: translate('crm.customerLearning.fields.type', 'Type'),
              description: <OptionalValue fallback={fallback} value={log.type} />,
            },
            {
              id: `${log.id}-date`,
              term: translate('crm.customerLearning.fields.attemptDate', 'Attempt date'),
              description: (
                <OptionalValue fallback={fallback} value={formatDate(log.attempted_at, locale)} />
              ),
            },
            {
              id: `${log.id}-passed`,
              term: translate('crm.customerLearning.fields.passedDate', 'Passed date'),
              description: (
                <OptionalValue fallback={fallback} value={formatDate(log.passed_at, locale)} />
              ),
            },
            {
              id: `${log.id}-score`,
              term: translate('crm.customerLearning.fields.score', 'Score (points)'),
              description: <EmLogScore log={log} fallback={fallback} locale={locale} />,
            },
            {
              id: `${log.id}-documents`,
              term: translate('crm.customerLearning.fields.documents', 'Documents'),
              description: (
                <EmLogDocumentCount
                  documentCount={log.document_count}
                  fallback={fallback}
                  locale={locale}
                />
              ),
            },
            {
              id: `${log.id}-instructor`,
              term: translate('crm.customerLearning.fields.instructor', 'Instructor'),
              description: <OptionalValue fallback={fallback} value={log.instructor?.name} />,
            },
          ]}
        />
      </CardContent>
    </Card>
  );
}
